// src/contexts/SessionContext.tsx
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { sessionReducer, initialSessionState, SessionState, SessionAction } from '../reducers/SessionReducer';

type SessionContextType = {
  sessionState: SessionState;
  dispatch: React.Dispatch<SessionAction>;
};

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const loadState = (): SessionState => {
  try {
    const serializedState = localStorage.getItem('sessionState');
    if (serializedState === null) {
      return initialSessionState;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error("Could not load state", err);
    return initialSessionState;
  }
};

const saveState = (sessionState: SessionState) => {
  try {
    const serializedState = JSON.stringify(sessionState);
    localStorage.setItem('sessionState', serializedState);
  } catch (err) {
    console.error("Could not save state", err);
  }
};

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionState, dispatch] = useReducer(sessionReducer, loadState());

  useEffect(() => {
    saveState(sessionState);
  }, [sessionState]);

  return (
    <SessionContext.Provider value={{ sessionState, dispatch }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
